import type { ListItem } from './type';

type EachCallback<T, U = void> = (value: T, index: number) => U;

export class ItemNodeList<T extends Record<string, any> = {}> {
  list: ItemNode<T>[] = [];

  constructor(public orgList: ListItem<T>[]) {
    let lastNode: Nullish<ItemNode<T>>;

    this.list = orgList.map(item => {
      const node = new ItemNode(item);

      if (lastNode) {
        lastNode.next = node;
        node.prev = lastNode;
      }

      lastNode = node;

      return node;
    });
  }

  get head() {
    return this.list[0];
  }

  get length() {
    return this.list.length;
  }

  private _each<U>(
    callback: EachCallback<ItemNode<T>, U>,
    breakCondition?: EachCallback<ItemNode<T>, boolean>,
  ) {
    let node: Nullish<ItemNode<T>> = this.head;
    let index = 0;

    while (node) {
      callback(node, index++);

      if (breakCondition?.(node, index)) break;

      node = node.next;
    }
  }

  forEach(callback: EachCallback<ItemNode<T>>) {
    return this._each(callback);
  }

  map<U>(callback: EachCallback<ItemNode<T>, U>) {
    const resultList = [] as U[];

    this._each((itemNode, index) => {
      resultList.push(callback(itemNode, index));
    });

    return resultList;
  }

  find(callback: EachCallback<ItemNode<T>, boolean>): Nullish<ItemNode<T>> {
    let findedItemNode: Nullish<ItemNode<T>> = null;

    this._each((itemNode, index) => {
      const result = callback(itemNode, index);

      if (result) {
        findedItemNode = itemNode;
      }

      return result;
    }, callback);

    return findedItemNode;
  }

  findIndex(callback: EachCallback<ItemNode<T>, boolean>) {
    let findedIndex = -1;

    this._each((itemNode, index) => {
      const result = callback(itemNode, index);

      if (result) {
        findedIndex = index;
      }

      return result;
    }, callback);

    return findedIndex;
  }

  filter(callback: EachCallback<ItemNode<T>, boolean>) {
    const resultList = [] as ItemNode<T>[];

    this._each((itemNode, index) => {
      const result = callback(itemNode, index);

      if (result) {
        resultList.push(itemNode);
      }

      return result;
    }, callback);

    return resultList;
  }

  findByUuid(uuid: ListItem['uuid']) {
    return this.find(itemNode => itemNode.value.uuid === uuid);
  }

  findIndexByUuid(uuid: ListItem['uuid']) {
    return this.findIndex(itemNode => itemNode.value.uuid === uuid);
  }

  getAt(index: number): Nullish<ItemNode<T>> {
    return this.list[index];
  }

  last() {
    return this.getAt(this.list.length - 1);
  }
}

export class ItemNode<T extends Record<string, any> = {}> {
  prev?: ItemNode<T>;

  next?: ItemNode<T>;

  constructor(public value: ListItem<T>) {}
}

export default function createItemNodeList<T extends Record<string, any> = {}>(
  itemList: ListItem<T>[],
) {
  const itemNodeList = new ItemNodeList(itemList);

  return itemNodeList;
}
